import { Button, Dropdown, Icon, MessageBar, MessageBarType } from "@fluentui/react";
import MessageCourier from "../../lib/MessageCourier";
import TimeTaxSupportSystemClient from "../../system";
import { User } from "../../system/User";
import i18next, { t } from 'i18next';
import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import "@glideapps/glide-data-grid/dist/index.css";
import DataEditor, { GridCellKind, Item, GridCell, GridColumn, DataEditorRef } from "@glideapps/glide-data-grid"
import { IMenuInactivo, IMenuPermisionBulkItem, IMenuPermisionRow } from "../../system/menuPermision/MenuPermisionSystem";
import { handleColumnResizeUtil } from "../../lib/GlideDataGrid/UtilsGlideGrid";

interface ISaleList {
  system: TimeTaxSupportSystemClient;
  changeBlockUI: (state: boolean) => void;
  courier: MessageCourier;
  user?:User;
  refresh?: boolean;
  setItems?: React.Dispatch<React.SetStateAction<any[]>>;
}

export interface ISaleCompanyJson {
  empven_id?: string;
  empven_no_distribuir: boolean;
  empven_emp_id: string;
  empven_periodo: string;
  empven_valor_venta: string;
  empven_owner_colid: string;
  empven_fecha_actualizacion: string;
  empven_fecha_registro: string;
  empven_monedaid: number;
}

type SortDirection = "asc" | "desc";

interface ISortConfig {
  columnId: string;
  direction: SortDirection;
}

const SORTABLE_COLUMNS = ["nombre_menu", "cod_menu", "cod_menu_root"];

interface MenuPermissionGrid {
  count: number,
  data:any[],
  recordsFiltered: number,
  recordsTotal: number
}

export const PermissionMenuView = (props: ISaleList) => {

    const [toastExit,setToastExit] = useState<boolean>(false);
    const [menuPermission, setMenuPermission] = useState<MenuPermissionGrid>({
      count: 0,
      data: [],
      recordsFiltered: 0,
      recordsTotal: 0,
    });
    const [clientSelect, setClientSelect] = useState<any>(null);
    const [originalData, setOriginalData] = useState<IMenuPermisionRow[]>([]);
    const [pendingChanges, setPendingChanges] = useState<{ cod_menu: string; per_id: string; active: number }[]>([]);
    const [menusInactivos, setMenusInactivos] = useState<IMenuInactivo[]>([]);
    const [columns, setColumns] = React.useState<GridColumn[]>([]);
    const [refresh, setRefresh] = useState<boolean>(false);
    const [sortConfig, setSortConfig] = useState<ISortConfig | null>(null);
    const ref = useRef<DataEditorRef>(null);
    const gridHeight = 600;

    const sortedData = useMemo(() => {
      if (!sortConfig) return menuPermission.data;
      const { columnId, direction } = sortConfig;
      return [...menuPermission.data].sort((a, b) => {
        const result = String(a[columnId] ?? "").localeCompare(
          String(b[columnId] ?? ""),
          undefined,
          { numeric: true, sensitivity: "base" }
        );
        return direction === "asc" ? result : -result;
      });
    }, [menuPermission.data, sortConfig]);

    const displayColumns = useMemo(
      () =>
        columns.map((c) =>
          sortConfig && c.id === sortConfig.columnId
            ? { ...c, title: `${c.title} ${sortConfig.direction === "asc" ? "▲" : "▼"}` }
            : c
        ),
      [columns, sortConfig]
    );

    // Ciclo de ordenamiento: ascendente -> descendente -> sin ordenar
    const handleHeaderClicked = (col: number) => {
      const columnId = columns[col]?.id;
      if (!columnId || !SORTABLE_COLUMNS.includes(columnId)) return;

      setSortConfig((prev) => {
        if (!prev || prev.columnId !== columnId) return { columnId, direction: "asc" };
        if (prev.direction === "asc") return { columnId, direction: "desc" };
        return null;
      });
    };

    const getCellContent = ([col, row]: Item): GridCell => {

      const colDef = columns[col];
      const item = sortedData[row] || {};
      const perfilFind = item.perfiles.find(perf => perf.puxam_perid === colDef.id);
      const isPermissionMenu = perfilFind ? Number(perfilFind.puxam_activo) === 1 : false;

      switch (col) {
        case 0:
          return {
            kind: GridCellKind.Text,
            data: "",
            displayData: String(item.nombre_menu),
            allowOverlay: false,
          };
        case 1:
          return {
            kind: GridCellKind.Text,
            data: "",
            displayData: String(item.cod_menu),
            allowOverlay: false,
          };
        case 2:
          return {
            kind: GridCellKind.Text,
            data: "",
            displayData: String(item.cod_menu_root),
            allowOverlay: false,
          };
        default:
          return {
            kind: GridCellKind.Boolean,
            data: isPermissionMenu,
            allowOverlay: false,
            readonly: false,
          };
      }
    };

    const getListPerfiles = async () => {
      try {
        props.changeBlockUI(true);
        const data = await props.system
          ?.getMenuPermissionSystem()
          .getListPerfiles();

        if (!data) return;

        const newColumns = data.map((perfil: any) => ({
          id: perfil.per_id,
          title: perfil.per_nombre,
          group: t("Perfiles"),
        }));

        const colMenu = [
          {
            id: "nombre_menu",
            title: t("Nombre pantalla"),
            width: 220,
            group: t("Menu"),
          },
          {
            id: "cod_menu",
            title: t("Codigo"),
            width: 80,
            group: t("Menu"),
          },
          {
            id: "cod_menu_root",
            title: t("Codigo padre"),
            width: 110,
            group: t("Menu"),
          }
        ];

        setColumns([
          ...colMenu,
          ...newColumns,
        ]);

      } catch (error) {
        console.log("error fetching tipo concept", error);
      } finally {
        props.changeBlockUI(false);
      }
    };

    const getMenuPermission = async () => {
      try {
        props.changeBlockUI(true);
        if (clientSelect) {
          const data = await props.system?.getMenuPermissionSystem().getDataMenuPermission(clientSelect.cliente_id);
          if (data) {
            setMenuPermission(data);
            setOriginalData(JSON.parse(JSON.stringify(data.data)));
            setPendingChanges([]);
          }
        }
      } catch (error) {
        console.log("error fetching tipo concept", error);
      } finally {
        props.changeBlockUI(false);
      }
    }

    const getListClientes = async () => {
      try {
        props.changeBlockUI(true);
        const data = await props.system?.getMenuPermissionSystem().getListClients();
        setClientSelect(data);
      } catch (error) {
        console.log("error fetching clients", error);
      } finally {
        props.changeBlockUI(false);
      }
    }

    const getListMenusInactivos = async () => {
      try {
        props.changeBlockUI(true);
        const data = await props.system.getMenuPermissionSystem().getListMenusInactivos();
        setMenusInactivos(data);
      } catch (error) {
        console.log("error fetching menus inactivos", error);
      } finally {
        props.changeBlockUI(false);
      }
    }

    const syncPending = (cod_menu: string, per_id: string, active: number) => {
      const originalItem = (originalData || []).find(o => o.cod_menu === cod_menu);
      const originalPerf = originalItem?.perfiles?.find(op => op.puxam_perid === per_id);
      const originalActive = originalPerf ? Number(originalPerf.puxam_activo) : 0;

      setPendingChanges(prev => {
        const filtered = prev.filter(pc => !(pc.cod_menu === cod_menu && pc.per_id === per_id));
        if (originalActive === active) return filtered;
        return [...filtered, { cod_menu, per_id, active }];
      });
    };

    const handleCellEdited = (
      cell: readonly [number, number],
      newValue: any,
    ) => {
      const [col, row] = cell;
      const colDef = columns[col];
      const rowDef = sortedData[row];
      if (!colDef || !rowDef) return;

      const active = newValue.data ? 1 : 0;

      setMenuPermission(prev => {
        const updatedRows = prev.data.map(item => {
          if (item.cod_menu !== rowDef.cod_menu) return item;
          const perfiles = item.perfiles ? [...item.perfiles] : [];
          const idx = perfiles.findIndex(perf => perf.puxam_perid === colDef.id);
          if (idx >= 0) {
            perfiles[idx] = { ...perfiles[idx], puxam_activo: String(active) };
          } else {
            perfiles.push({
              puxam_id: 'new',
              puxam_codmenu: item.cod_menu,
              puxam_perid: colDef.id,
              nombre_menu: item.nombre_menu,
              puxam_activo: String(active),
              padre: item.menu_padre ?? 'N/A',
            });
          }
          return { ...item, perfiles };
        });
        return { ...prev, data: updatedRows };
      });

      syncPending(rowDef.cod_menu, String(colDef.id), active);
    };

    const hasChanges = pendingChanges.length > 0;

    const handleSavePermissions = async () => {
      const menuSystem = props.system?.getMenuPermissionSystem();
      if (!menuSystem || !clientSelect) return;

      const payload: IMenuPermisionBulkItem[] = pendingChanges.map(pc => {
        const row = menuPermission.data.find(item => item.cod_menu === pc.cod_menu);
        const perf = row?.perfiles?.find(p => p.puxam_perid === pc.per_id);
        const isNew = !perf || perf.puxam_id === 'new';

        return {
          puxam_id: isNew ? 'new' : perf!.puxam_id,
          puxam_codmenu: pc.cod_menu,
          puxam_perid: Number(pc.per_id),
          puxam_clienteid: clientSelect.cliente_id,
          puxam_activo: pc.active,
        };
      });

      if (payload.length === 0) {
        props.courier.messageInfo(t('No hay cambios para guardar'));
        return;
      }

      props.changeBlockUI(true);
      try {
        const response = await menuSystem.saveMenuPermissionsBulk(payload);
        const fallidos = response.resultados.filter(r => !r.ok);

        if (response.exitosos > 0 && fallidos.length === 0) {
          props.courier.messageSuccess(`${response.exitosos} ${t('permisos se insertaron correctamente.')}`);
        } else if (response.exitosos === 0 && fallidos.length > 0) {
          props.courier.messageError(`${t('No se pudieron insertar')} ${response.fallidos} ${t('permisos. Por favor, intente nuevamente.')}`);
        } else if (response.exitosos > 0 && fallidos.length > 0) {
          props.courier.messageWarning(
            `${response.exitosos} ${t('permisos se insertaron correctamente y')} ${response.fallidos} ${t('no se pudieron insertar. Revise el detalle e intente nuevamente.')}`
          );
        }

        setRefresh(!refresh);
        await getMenuPermission();
      } catch (error) {
        console.log("error saving menu permissions", error);
        props.courier.messageError(error);
      } finally {
        props.changeBlockUI(false);
      }
    }

    useEffect(() => {
      getListPerfiles();
      getListClientes();
      getListMenusInactivos();
    }, [refresh]);

    useEffect(() => {
      if (clientSelect) {
        getMenuPermission();
      }
    }, [clientSelect]);

    const handleColumnResize = React.useCallback(
      handleColumnResizeUtil(setColumns),
      [setColumns]
    );

    return (
      <div>
        {toastExit && (
          <div className="toast_container_collaborator">
            <div className="toast_message_collaborator">
              <Icon iconName="Completed" className="icon_toast_collaborator" />
              {t("Se registro l exitosamente")}
            </div>
            <Icon iconName="Clear" onClick={() => setToastExit(false)} />
          </div>
        )}
        <div className="collaborator_create_hader_buton" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>
            <Icon
              iconName="ReminderPerson"
              style={{ color: "#0078D4", marginRight: "5px" }}
            />
            <span>{t("Menu de permisos")}</span>
          </span>
          <Button
            text={t("Guardar")}
            iconProps={{ iconName: "Save" }}
            primary
            disabled={!hasChanges}
            onClick={() => void handleSavePermissions()}
          />
        </div>
        {clientSelect ? (
          <div style={{ margin: "10px" }}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: "10px" }}>
              <Dropdown
                label={t("Cliente")}
                placeholder={clientSelect ? clientSelect.cliente_nombre : ""}
                options={[]}
                disabled
                onChange={(event, option) => setClientSelect(option?.data)}
                style={{
                  width: "500px",
                }}
              />
              <MessageBar
                messageBarType={MessageBarType.info}
                styles={{ root: { marginBottom: 0, flex: 1, minWidth: "300px" } }}
              >
                {`${t("Los codigos de menus estan desativados")}: ${menusInactivos
                  .map((menu) => `${menu.cod_menu} (${menu.nombre_menu})`)
                  .join(", ")}`}
              </MessageBar>
            </div>
            <br />
            <DataEditor
              ref={ref}
              getCellContent={getCellContent}
              columns={displayColumns}
              rows={sortedData.length}
              smoothScrollX
              smoothScrollY
              freezeColumns={3}
              rowSelect="multi"
              columnSelect="multi"
              onCellEdited={handleCellEdited}
              onHeaderClicked={handleHeaderClicked}
              getCellsForSelection={true}
              onColumnResize={handleColumnResize}
              width={"100%"}
              height={gridHeight}
            />
          </div>
        ) : null}
      </div>
    );
}
